import { Box, Typography, Stack, Button } from "@mui/material";
import { User, Loader, ArrowRight } from "lucide-react";
import "../assignNurseModal.scss";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { getHeaders, URI_API } from "../../../utils/config";
import { OptionCategory, OptionRange } from "../../../interfaces/OptionCategory";
import { PatientsListContext } from "../../../components/PatientsList/PatientsList";
import { categoryReducer } from "../categoryReducer";

interface ModalCategoryBodyProps {
	handleTab: () => void;
	setIsEdit: (value: boolean) => void;
	activeItems: any;
	categoryState: any;
	dispatchCategory: any;
}

export default function ModalCategoryBody({
	handleTab,
	setIsEdit,
	activeItems,
	categoryState,
	dispatchCategory,
}: ModalCategoryBodyProps) {
	const [categoryInformation, setCategoryInformation] = useState<OptionCategory[]>([]);
	const [categoryRange, setCategoryRange] = useState<OptionRange[]>([]);
	const { selectedPatient, setSelectedPatient, setCategoryList } =
		useContext(PatientsListContext);
	const [isDisabled, setIsDisabled] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const buttonRefs = useRef<HTMLButtonElement[]>([]);

	const getCategoryInformation = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(
                `${URI_API}/patient_checklist_init/${selectedPatient?.id}?type=general`,
                getHeaders()
            );
            const responseBody = await response.json();

            if (!response.ok) {
                console.error("Error del backend:", responseBody);
                setIsLoading(false);
                return;
            }

            const { 
                active_items = [], 
                ranges = [], 
                checklist = [] 
            } = responseBody;

            const formattedRanges = ranges
                .map((category_range: any) => ({
                    id: category_range.id,
                    description: category_range.description,
                    category: category_range.category,
                    min: parseFloat(category_range.min),
                    max: parseFloat(category_range.max),
                }))
                .sort((a: OptionRange, b: OptionRange) => a.min - b.min);
            setCategoryRange(formattedRanges);

            // 3. PROCESAR CHECKLIST Y ITEMS ACTIVOS
            const activeIds = new Set(active_items.map((i: any) => i.id));

            const processedCategories = checklist.map((cat: any) => ({
                id: cat.id,
                category: cat.category,
                subcategory: cat.subcategory,
                description: cat.description,
                value: cat.value,
                checked: activeIds.has(cat.id),
            }));

            // Actualizar estado local y contexto
            setCategoryList(processedCategories);
			console.log("Processed Categories:", processedCategories);
            setCategoryInformation(processedCategories);

            // Inicializar Reducer
            dispatchCategory({
                type: "INITIALIZE_OPTIONS",
                payload: {
                    categories: processedCategories.map((cat: any) => ({
                        id: cat.id,
                        value: cat.value,
                        checked: cat.checked,
                    })),
                },
            });

            if (selectedPatient?.category_id !== null) {
                setIsEdit(true);
            }
        } catch (error) {
            console.error("Error loading category info", error);
        } finally {
            setIsLoading(false);
        }
    };

	useEffect(() => {
		getCategoryInformation();
	}, []);

	const getCategoryLabel = (): string => {
		let label = "";

		categoryRange.forEach((category_range: OptionRange) => {
			if (
				categoryState.count >= category_range.min &&
				categoryState.count <= category_range.max
			) {
				label = category_range.description;
			}
		});

		return label;
	};

	useEffect(() => {
		setCategoryList((prev: any) =>
			prev.map((item: any) => ({
				...item,
				checked: categoryState.selectedOptionsCategory.includes(item.id),
			})),
		);
	}, [categoryState.selectedOptionsCategory]);

	useEffect(() => {
		setIsDisabled(categoryState.selectedOptionsCategory.length === 0);
	}, [categoryState.selectedOptionsCategory]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (categoryInformation?.length) {
				if ((e.key === "ArrowDown" || e.key === "ArrowUp") && focusedIndex === null) {
					e.preventDefault();
					setFocusedIndex(0);
				} else if (e.key === "ArrowDown" && focusedIndex !== null) {
					e.preventDefault();
					setFocusedIndex((prev) =>
						Math.min((prev ?? 0) + 1, categoryInformation.length - 1),
					);
				} else if (e.key === "ArrowUp" && focusedIndex !== null) {
					e.preventDefault();
					setFocusedIndex((prev) => Math.max((prev ?? 0) - 1, 0));
				} else if ((e.key === "Enter" || e.key === " ") && focusedIndex !== null) {
					e.preventDefault();
					buttonRefs.current[focusedIndex]?.click();
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [focusedIndex, categoryInformation]);

	useEffect(() => {
		if (focusedIndex !== null) {
			buttonRefs.current[focusedIndex]?.focus();
		}
	}, [focusedIndex]);

	const buildOptions = () => {
		if (!categoryInformation) return <></>;
		return categoryInformation.map((category_information: OptionCategory, index: number) => {
			const id = category_information.id;
			const isSelected = categoryState.selectedOptionsCategory.includes(id);

			const isFocused = focusedIndex === index;

			return (
				<Button
					key={category_information.id}
					ref={(el) => (buttonRefs.current[index] = el!)}
					tabIndex={isFocused ? 0 : -1}
					className={
						isSelected ? "stack-item-container-selected" : "stack-item-container"
					}
					onClick={(e) => {
						dispatchCategory({
							type: "TOGGLE_OPTION",
							payload: {
								id: category_information.id,
								value: category_information.value,
							},
						});
						e.currentTarget.blur();
					}}
					disableRipple
				>
					<Loader
						className={
							isSelected
								? "stack-item-container-icon-selected"
								: "stack-item-container-icon"
						}
					/>
					<Typography className="stack-item-container-text">
						{category_information.description}
					</Typography>
				</Button>
			);
		});
	};

	const getCategoryId = (): string => {
		let id = "";
		categoryRange.map((range: OptionRange) => {
			if (categoryState.count >= range.min && categoryState.count <= range.max) {
				id = range.id;
			}
		});

		return id;
	};

	const getCategoryDescription = (): string => {
		let description = "";
		categoryRange.map((range: OptionRange) => {
			if (categoryState.count >= range.min && categoryState.count <= range.max) {
				description = range.description;
			}
		});
		return description;
	};

	const handleNext = () => {
		setSelectedPatient({
			...selectedPatient,
			categoryid: getCategoryId(),
			categoryname: getCategoryDescription(),
		});
		handleTab();
	};

	return (
		<>
			<Box>
				<Box>
					{isLoading ? (
						<Box>
							<Box className="category-loader-container">
								<Box className="category-loader-container__loader"></Box>
							</Box>
						</Box>
					) : (
						<Box>
							<Box className="modal-tab-content__patient-info">
								<Box sx={{ display: "flex" }}>
									<Typography className="modal-tab-content__patient-info__tag">
										Paciente:
									</Typography>
									<Typography className="modal-tab-content__patient-info__info">
										<User className="modal-tab-content__patient-info__info-user-icon" />
										{selectedPatient?.name}{" "}
									</Typography>
									<Typography
										sx={{ marginLeft: "30px" }}
										className="modal-tab-content__patient-info__tag"
									>
										Cama:
									</Typography>
									<Typography className="modal-tab-content__patient-info__info">
										{selectedPatient?.room}
									</Typography>
								</Box>
								<Box sx={{ display: "flex" }}>
									<Typography className="modal-tab-content__patient-info__tag">
										Categoría:
									</Typography>
									<Typography className="modal-tab-content__patient-info__info">
										{getCategoryLabel()}
									</Typography>
								</Box>
							</Box>
							<Box className="modal-tab-content__options">
								<Stack spacing={1}>{buildOptions()}</Stack>
							</Box>
							<Box className="modal-footer">
								<Button
									variant="outlined"
									className="modal-footer-button"
									onClick={(e) => {
										handleNext(), e.currentTarget.blur();
									}}
									disableRipple
									disabled={isDisabled}
								>
									<Typography className="modal-footer-button-label">
										Siguiente
									</Typography>
									<ArrowRight className="modal-footer-button-icon" />
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</>
	);
}
